import { differenceInCalendarDays, isValid, startOfDay } from 'date-fns'

export type TFilterableLease = {
  leaseType?: string
  leaseTerms?: {
    endDate?: string
    rolloverMonthToMonth?: boolean
  }
}

/** The tab keys `getPortfolioFilters` produces, in the order it produces them. */
export const LEASE_TAB_KEYS = [
  'all',
  'draft',
  'active',
  'expiring',
  'renewals',
  'expired',
  'terminated',
  'deleted',
] as const

export type TLeaseTabKey = typeof LEASE_TAB_KEYS[number]

export type TLeaseFilterPill = {
  key: string
  label: string
  matches: (lease: TFilterableLease) => boolean
}

export type TLeaseFilterGroup = {
  /** Doubles as the query string param name. */
  key: string
  label: string
  /** Tabs the group appears on. Absent means every tab. */
  tabs?: TLeaseTabKey[]
  pills: TLeaseFilterPill[]
}

/** One pill per group: the group's key maps to the selected pill's key, or to nothing. */
export type TLeaseFilterSelection = Record<string, string | undefined>

export const LEASE_TYPE_GROUP_KEY = 'leaseType'
export const EXPIRING_IN_GROUP_KEY = 'expiringIn'

export const isMonthToMonth = (lease: TFilterableLease): boolean => lease.leaseTerms?.rolloverMonthToMonth ?? false

/**
 * Days from today to the lease's end date, or undefined if it has no usable one. Negative for a
 * lease whose end date has already passed, which an expiring lease's can have.
 */
const daysToEndDate = (lease: TFilterableLease): number | undefined => {
  const endDate = lease.leaseTerms?.endDate ? new Date(lease.leaseTerms.endDate) : undefined
  return endDate && isValid(endDate) ? differenceInCalendarDays(startOfDay(endDate), startOfDay(new Date())) : undefined
}

/**
 * The windows don't overlap: a lease lands in exactly one of them, so the three counts add up to
 * the leases due in the next 90 days rather than counting the nearest ones three times. The first
 * window is open-ended at the bottom, so a lease whose end date has already passed sits there.
 *
 * Month-to-month leases are deliberately excluded from the day windows. They keep the Expiring
 * status and their original end date, but they have rolled over rather than run out, so counting
 * them as "expiring in 30 days" would be wrong on both sides: they aren't ending then, and they
 * would crowd out the leases that are.
 */
const expiresBetween =
  (from: number, to: number) =>
  (lease: TFilterableLease): boolean => {
    if (isMonthToMonth(lease)) return false
    const remaining = daysToEndDate(lease)
    return remaining !== undefined && remaining >= from && remaining <= to
  }

export const LEASE_FILTER_GROUPS: TLeaseFilterGroup[] = [
  {
    key: LEASE_TYPE_GROUP_KEY,
    label: 'Lease type',
    pills: [
      { key: 'managed', label: 'Managed', matches: lease => lease.leaseType === 'Managed' },
      { key: 'unmanaged', label: 'Unmanaged', matches: lease => lease.leaseType === 'Unmanaged' },
    ],
  },
  {
    key: EXPIRING_IN_GROUP_KEY,
    label: 'Expiring in',
    // Only meaningful where every lease is on its way out; elsewhere the window says nothing.
    tabs: ['expiring'],
    pills: [
      { key: '30-days', label: '30 days', matches: expiresBetween(-Infinity, 30) },
      { key: '31-60-days', label: '60 days', matches: expiresBetween(31, 60) },
      { key: '61-90-days', label: '90 days', matches: expiresBetween(61, 90) },
      { key: 'month-to-month', label: 'Month-to-month', matches: isMonthToMonth },
    ],
  },
]

export const getLeaseFilterGroups = (tabKey: string): TLeaseFilterGroup[] =>
  LEASE_FILTER_GROUPS.filter(group => !group.tabs || group.tabs.includes(tabKey as TLeaseTabKey))

type TApplyOptions = {
  tabKey: string
  excludeGroupKey?: string
}

export const applyLeaseFilterPills = <T extends TFilterableLease>(
  leases: T[],
  selection: TLeaseFilterSelection,
  { tabKey, excludeGroupKey }: TApplyOptions,
): T[] =>
  getLeaseFilterGroups(tabKey).reduce((remaining, group) => {
    if (group.key === excludeGroupKey) {
      return remaining
    }

    const selected = group.pills.find(pill => pill.key === selection[group.key])

    return selected ? remaining.filter(lease => selected.matches(lease)) : remaining
  }, leases)

export const countLeasesForPill = (leases: TFilterableLease[], pill: TLeaseFilterPill): number =>
  leases.filter(pill.matches).length
